<script setup lang="ts">
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { getTranslatedSettings } from '@nova/core';

const { t } = useI18n();

type GateManagementReferenceSettingsModel = {
  gateManagementDriverReferenceRequired: boolean;
  gateManagementDriverReferenceValidated: boolean;
};

const props = withDefaults(
  defineProps<{
    entitySettings: GateManagementReferenceSettingsModel;
    isGateManagementEnabled: boolean;
    readOnly: boolean;
  }>(),
  {
    readOnly: false
  }
);

const localSettings = computed(() => props.entitySettings);

const settings = computed(() => {
  const all = getTranslatedSettings('Warehouse');
  return {
    gateManagementDriverReferenceRequired: all.gateManagementDriverReferenceRequired,
    gateManagementDriverReferenceValidated: all.gateManagementDriverReferenceValidated
  };
});

watch(
  () => localSettings.value.gateManagementDriverReferenceRequired,
  required => {
    if (!required) {
      localSettings.value.gateManagementDriverReferenceValidated = false;
    }
  }
);
</script>

<template>
  <div v-if="props.isGateManagementEnabled" class="d-flex flex-column my-4">
    <div>
      <h4 class="mb-1">
        {{ t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.title') }}
      </h4>
      <p class="typography-body-md">
        {{
          t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.description')
        }}
      </p>
    </div>

    <div class="d-flex flex-column gap-4 mt-5">
      <span class="typography-body-sm-bold">
        {{
          t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.driversLabel')
        }}
      </span>

      <div>
        <p class="typography-body-sm mb-2">
          {{ settings.gateManagementDriverReferenceRequired.label }}
        </p>
        <div class="gm-switch-row d-flex align-center pa-1 gap-3">
          <v-switch
            v-model="localSettings.gateManagementDriverReferenceRequired"
            color="primary"
            hide-details
            :disabled="readOnly"
            data-testid="driver-reference-required-switch" />
          <span class="typography-body-md">
            {{
              t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.required')
            }}
          </span>
        </div>
      </div>

      <div>
        <p class="typography-body-sm mb-2">
          {{ settings.gateManagementDriverReferenceValidated.label }}
        </p>
        <div class="gm-switch-row d-flex align-center pa-1 gap-3">
          <v-switch
            v-model="localSettings.gateManagementDriverReferenceValidated"
            color="primary"
            hide-details
            :disabled="readOnly || !localSettings.gateManagementDriverReferenceRequired"
            data-testid="driver-reference-validated-switch" />
          <span
            :class="
              !localSettings.gateManagementDriverReferenceRequired
                ? 'typography-body-md text-disabled'
                : 'typography-body-md'
            ">
            {{
              t('warehouse.pages.settings.expansionPanels.gateManagement.referenceNumber.validated')
            }}
          </span>
        </div>
      </div>
    </div>

    <v-divider />
  </div>
</template>

<style scoped lang="scss">
.gm-switch-row :deep(.v-switch__track) {
  height: 14px;
  min-width: 36px;
  border-radius: 7px;
  opacity: 1;
}

.gm-switch-row :deep(.v-switch__thumb) {
  width: 20px;
  height: 20px;
}

.gm-switch-row :deep(.v-selection-control--checked .v-switch__track) {
  background-color: $color-primary-60;
}

.gm-switch-row :deep(.v-selection-control--checked .v-switch__thumb) {
  background-color: $color-primary-100;
}

.gm-switch-row :deep(:not(.v-selection-control--checked) .v-switch__track) {
  background-color: $color-neutral-40;
}

.gm-switch-row :deep(:not(.v-selection-control--checked) .v-switch__thumb) {
  background-color: $color-background-primary;
}
</style>
